import React, { useState, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { ReviewCaveat, GeneratedMark, GeneratedLegend } from '../components/ReviewAid';
import SocialAnalysis, { socialNav, isAnalysisPage } from '../components/analysis/SocialAnalysis';
import { instagramInput } from '../utils/socialAnalysis';
import {
  parseInstagramReturn, SECTION_KEYS, MESSAGE_SECTION_KEYS, IP_KEYS,
  MEDIA_SECTION_KEYS, FOLLOWER_SECTION_KEYS, COMMENT_SECTION_KEYS, LIKES_SECTION_KEYS, DEVICES_SECTION_KEYS,
} from '../utils/instagramWarrant';

const TRANSLATABLE_LABELS  = new Set(['Body','Message','Title','Description','Caption','Text']);

// ─── Media from the return's folder (browser only) ──────────────────────────

// Build { "linked_media/file.jpg" → objectURL, "file.jpg" → objectURL }
function buildMediaMap(files) {
  const map = {};
  for (const f of Array.from(files)) {
    const url = URL.createObjectURL(f);
    map[f.name] = url;
    if (f.webkitRelativePath) {
      map[f.webkitRelativePath] = url;
      // Store as "linked_media/filename" if path is deeper
      const parts = f.webkitRelativePath.split('/');
      if (parts.length >= 2) map[parts.slice(-2).join('/')] = url;
    }
  }
  return map;
}

function resolveMedia(src, mediaMap) {
  if (!src || !mediaMap) return null;
  return mediaMap[src] || mediaMap[src.split('/').pop()] || null;
}

// ─── Color constants ──────────────────────────────────────────────────────────

const C = {
  bg:      '#0f172a',
  surface: '#1e293b',
  surf2:   '#263044',
  border:  '#334155',
  text:    '#e2e8f0',
  muted:   '#94a3b8',
  indigo:  '#4f46e5',
  cyan:    '#67e8f9',
  pink:    '#f472b6',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function NavBtn({ label, badge, active, onClick }) {
  return (
    <div style={{ padding: '2px 8px' }}>
      <button onClick={onClick} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: '7px 10px', border: 'none', borderRadius: 6,
        background: active ? C.pink : 'transparent',
        color: active ? '#fff' : C.muted,
        textAlign: 'left', cursor: 'pointer', fontSize: 13,
        transition: 'background .15s, color .15s',
      }}>
        {label}
        {badge !== undefined && (
          <span style={{ marginLeft: 'auto', fontSize: 11, background: 'rgba(255,255,255,.15)', padding: '1px 6px', borderRadius: 10 }}>
            {badge}
          </span>
        )}
      </button>
    </div>
  );
}

function StatCard({ number, label, highlight, color }) {
  return (
    <div style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
      <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: color || (highlight ? '#f9a8d4' : C.text) }}>
        {number}
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: C.muted, marginBottom: 14 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value, mono, first }) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '5px 0', fontSize: 13, borderTop: first ? 'none' : `1px solid ${C.border}` }}>
      <span style={{ color: C.muted, width: 140, flexShrink: 0 }}>{label}</span>
      <span style={{ wordBreak: 'break-word', fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? 12 : undefined }}>
        {value}
      </span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function InstagramScan() {
  const [phase, setPhase]                 = useState('upload');
  const [data, setData]                   = useState(null);
  const [error, setError]                 = useState('');
  const [file, setFile]                   = useState(null);
  const [dragOver, setDragOver]           = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarFilter, setSidebarFilter] = useState('');
  const [ipFilter, setIpFilter]           = useState('');
  const [ipSort, setIpSort]               = useState(null);
  const [followerFilter, setFollowerFilter] = useState('');
  const [commentFilter, setCommentFilter] = useState('');
  const [likesFilter, setLikesFilter]     = useState('');
  const [selectedThreads, setSelectedThreads] = useState({});
  const [chatSearches, setChatSearches]   = useState({});
  const [globalChatSearches, setGlobalChatSearches] = useState({});
  const [xlateModelStatus, setXlateModelStatus] = useState('idle');
  const [xlateModelProgress, setXlateModelProgress] = useState(0);
  const [xlateModelError, setXlateModelError] = useState('');
  const [xlateCache, setXlateCache]       = useState({});
  const [msgTranslateOn, setMsgTranslateOn] = useState({});
  const [xlateConfirmPending, setXlateConfirmPending] = useState(null);
  const [mediaMap, setMediaMap]           = useState({});
  const analysisInput = useMemo(() => (data ? instagramInput(data) : null), [data]);
  const [pdfExporting, setPdfExporting]   = useState(false);
  const xlatorRef     = useRef(null);
  const xlatorLoadRef = useRef(null);
  const xlateConfirmedRef = useRef(false);
  const fileInputRef  = useRef(null);
  const mediaInputRef = useRef(null);

  function ensurePipeline() {
    if (xlatorRef.current) return Promise.resolve(xlatorRef.current);
    if (xlatorLoadRef.current) return xlatorLoadRef.current;
    setXlateModelStatus('loading');
    setXlateModelProgress(0);
    setXlateModelError('');
    const loadPromise = (async () => {
      try {
        const { pipeline, env } = await import('@xenova/transformers');
        env.allowLocalModels = false;
        env.backends.onnx.wasm.numThreads = typeof SharedArrayBuffer !== 'undefined' ? 4 : 1;
        const translator = await pipeline('translation', 'Xenova/opus-mt-mul-en', {
          progress_callback: (p) => {
            if (p.status === 'downloading' || p.status === 'loading')
              setXlateModelProgress(Math.round((p.progress || 0) * 100));
          },
        });
        xlatorRef.current = translator;
        xlatorLoadRef.current = null;
        setXlateModelStatus('ready');
        setXlateModelProgress(100);
        return translator;
      } catch (err) {
        xlatorLoadRef.current = null;
        setXlateModelError(err?.message || String(err));
        setXlateModelStatus('error');
        return null;
      }
    })();
    xlatorLoadRef.current = loadPromise;
    return loadPromise;
  }

  async function batchTranslateTexts(items) {
    await new Promise(resolve => setTimeout(resolve, 0));
    const toTranslate = items.filter(({ cacheKey }) => xlateCache[cacheKey] === undefined);
    if (!toTranslate.length) return;
    setXlateCache(prev => {
      const next = { ...prev };
      toTranslate.forEach(({ cacheKey }) => { next[cacheKey] = 'pending'; });
      return next;
    });
    const translator = await ensurePipeline();
    if (!translator) {
      setXlateCache(prev => {
        const next = { ...prev };
        toTranslate.forEach(({ cacheKey }) => { next[cacheKey] = ''; });
        return next;
      });
      return;
    }
    const CHUNK = 8;
    for (let i = 0; i < toTranslate.length; i += CHUNK) {
      const chunk = toTranslate.slice(i, i + CHUNK);
      try {
        const results = await translator(chunk.map(c => c.text), { truncation: true });
        setXlateCache(prev => {
          const next = { ...prev };
          chunk.forEach(({ cacheKey }, j) => { next[cacheKey] = results[j]?.translation_text || ''; });
          return next;
        });
      } catch {
        setXlateCache(prev => {
          const next = { ...prev };
          chunk.forEach(({ cacheKey }) => { next[cacheKey] = ''; });
          return next;
        });
      }
    }
  }

  async function exportThreadPDF(thread, msgKey, selectedIdx) {
    setPdfExporting(true);
    try {
      await import('pdfmake/build/pdfmake');
      await import('pdfmake/build/vfs_fonts');
      const pdfMake   = window.pdfMake;
      const subjectId = data?.subject_id || '';
      const isXlateOn = !!(msgTranslateOn[msgKey]?.[selectedIdx]);
      const threadName = thread.thread_name || thread.participants[0] || `Thread ${thread.thread_id}`;
      const safeFileName = threadName.replace(/[^a-zA-Z0-9 _-]/g, '').trim() || `thread_${thread.thread_id}`;
      const bodyContent = [];
      for (const msg of thread.messages) {
        const isMine  = !!(subjectId && msg.author.includes(subjectId));
        const xlation = isXlateOn && msg.body && !msg.removed
          ? (xlateCache[msg.body] && xlateCache[msg.body] !== 'pending' ? xlateCache[msg.body] : null)
          : null;
        const stackItems = [];
        if (!isMine) stackItems.push({ text: msg.author, fontSize: 9, bold: true, color: '#be185d', margin: [0,0,0,2] });
        stackItems.push({ text: msg.removed ? '[removed by sender]' : (msg.body || '[no text]'), fontSize: 11, italics: !!msg.removed, color: isMine ? '#ffffff' : (msg.removed ? '#94a3b8' : '#1e293b') });
        if (xlation) stackItems.push({ text: `[Translation] ${xlation}`, fontSize: 9, italics: true, color: isMine ? '#fce7f3' : '#64748b', margin: [0,3,0,0] });
        const bubble = { table: { widths: ['*'], body: [[{ stack: stackItems, fillColor: isMine ? '#be185d' : '#dde3ed', margin: [10,7,10,7] }]] }, layout: 'noBorders', width: 315 };
        bodyContent.push({ columns: isMine ? [{ text: '', width: '*' }, bubble] : [bubble, { text: '', width: '*' }], columnGap: 0, margin: [0,4,0,0] });
        bodyContent.push({ text: msg.sent || '', fontSize: 8, color: '#94a3b8', alignment: isMine ? 'right' : 'left', margin: [0,2,0,6] });
      }
      const docDef = {
        pageMargins: [48, 52, 48, 52],
        defaultStyle: { font: 'Roboto' },
        content: [
          { text: 'Instagram Warrant Processor — Message Thread Export', fontSize: 9, color: '#94a3b8', margin: [0,0,0,4] },
          { text: threadName, fontSize: 18, bold: true, color: '#0f172a', margin: [0,0,0,6] },
          { text: `Participants: ${thread.participants.join(', ')}`, fontSize: 11, color: '#475569', margin: [0,0,0,3] },
          { text: `${thread.messages.length} messages · Thread ID: ${thread.thread_id}`, fontSize: 9, color: '#94a3b8', margin: [0,0,0,14] },
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 499, y2: 0, lineWidth: 0.5, lineColor: '#e2e8f0' }], margin: [0,0,0,14] },
          ...bodyContent,
        ],
      };
      pdfMake.createPdf(docDef).download(`${safeFileName}.pdf`);
    } catch (err) {
      alert('PDF export failed: ' + (err?.message || String(err)));
    } finally {
      setPdfExporting(false);
    }
  }

  function handleFile(f) {
    trackToolAction('/instagramscan', 'Instagram Warrant Processor');
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.html')) {
      setError('Please select the records.html file (must be a .html file).');
      return;
    }
    setFile(f);
    setError('');
  }

  async function handleParse() {
    if (!file) return;
    setPhase('parsing');
    setError('');
    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = e => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file, 'utf-8');
      });
      const parsed = parseInstagramReturn(text);
      setData(parsed);
      setActiveSection('overview');
      setSelectedThreads({});
      setChatSearches({});
      setGlobalChatSearches({});
      setIpFilter('');
      setIpSort(null);
      setFollowerFilter('');
      setCommentFilter('');
      setLikesFilter('');
      setXlateCache({});
      setMsgTranslateOn({});
      setPhase('report');
    } catch (err) {
      setError('Error parsing file: ' + err.message);
      setPhase('upload');
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }

  function handleNewFile() {
    setPhase('upload');
    setFile(null);
    setData(null);
    setError('');
    setMediaMap({});
  }

  function handleMediaFiles(files) {
    if (!files || !files.length) return;
    setMediaMap(buildMediaMap(files));
  }

  // ── Upload / parsing view ──────────────────────────────────────────────────

  if (phase !== 'report') {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        <Navbar pageTitle="Instagram Warrant Processor" />
        <div style={{ maxWidth: 620, margin: '0 auto', padding: '48px 24px 80px' }}>
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px', margin: 0 }}>Instagram Warrant Processor</h2>
            <p style={{ color: '#64748b', marginTop: 6, fontSize: 15, lineHeight: 1.55 }}>
              Select the <strong>records.html</strong> file from an Instagram search warrant return to browse account info,
              messages, IP addresses, photos, followers, and all data sections.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 24, padding: '13px 18px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, fontSize: 14, color: '#166534', lineHeight: 1.5 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span><strong>100% local &amp; private.</strong> Your file is parsed entirely in the browser. Nothing is sent to any server.</span>
          </div>
          <ReviewCaveat what="the actual returns from Meta (Instagram)" style={{ marginBottom: 24 }} />

          <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', padding: 32 }}>
            {/* Drop zone */}
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              style={{
                border: `2px dashed ${dragOver ? '#be185d' : '#fbcfe8'}`,
                borderRadius: 10, padding: '40px 24px', textAlign: 'center', cursor: 'pointer',
                background: dragOver ? '#fdf2f8' : 'transparent',
                transition: 'border-color .2s, background .2s',
              }}
            >
              <input ref={fileInputRef} type="file" accept=".html" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }} />
              <div style={{ width: 48, height: 48, borderRadius: 12, background: '#fdf2f8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', color: '#be185d' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Drop records.html here</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>or <span style={{ color: '#be185d', fontWeight: 600 }}>click to browse</span></div>
            </div>

            {file && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, padding: '12px 16px', background: '#fdf2f8', borderRadius: 8, border: '1px solid #fbcfe8' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#be185d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#9d174d', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                <span style={{ fontSize: 12, color: '#be185d', flexShrink: 0 }}>
                  {file.size >= 1048576 ? (file.size / 1048576).toFixed(1) + ' MB' : Math.round(file.size / 1024) + ' KB'}
                </span>
              </div>
            )}

            {/* Optional media folder */}
            <div style={{ marginTop: 16, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                Optional: Load linked_media folder
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10, lineHeight: 1.5 }}>
                Select the <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>linked_media</code> folder
                (or the entire warrant return folder) to display photos and videos inline.
              </div>
              <input ref={mediaInputRef} type="file" multiple
                /* webkitdirectory allows selecting entire folder */
                webkitdirectory=""
                style={{ display: 'none' }}
                onChange={e => handleMediaFiles(e.target.files)} />
              <button
                onClick={() => mediaInputRef.current?.click()}
                style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontWeight: 600 }}
              >
                {Object.keys(mediaMap).length > 0
                  ? `✓ ${Math.round(Object.keys(mediaMap).length / 2)} media files loaded`
                  : 'Select media folder…'}
              </button>
            </div>

            <button
              onClick={handleParse}
              disabled={!file || phase === 'parsing'}
              style={{
                display: 'block', width: '100%', marginTop: 20, padding: 13,
                background: '#be185d', color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 15, fontWeight: 700,
                cursor: !file || phase === 'parsing' ? 'not-allowed' : 'pointer',
                opacity: !file || phase === 'parsing' ? 0.55 : 1,
              }}
            >
              {phase === 'parsing' ? 'Parsing…' : 'Parse Warrant Return'}
            </button>

            {error && (
              <div style={{ marginTop: 16, padding: '12px 16px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, fontSize: 14 }}>
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Report view ───────────────────────────────────────────────────────────────

  const { summary, subject_id, sections, message_threads, ip_records, top_ips,
          followers, following, followReqs, photos, profilePic, videos, liveVideos,
          archivedLive, devices, comments, commentLikes, likes } = data;

  const totalThreads = MESSAGE_SECTION_KEYS.reduce((acc, k) => acc + (message_threads[k]?.length || 0), 0);
  const sidebarItems = SECTION_KEYS.filter(k => sections[k]?.count > 0);
  const filteredSidebar = sidebarFilter
    ? sidebarItems.filter(k => sections[k].label.toLowerCase().includes(sidebarFilter.toLowerCase()))
    : sidebarItems;

  const hasMediaLoaded = Object.keys(mediaMap).length > 0;

  return (
    <div style={{
      height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      background: C.bg, color: C.text,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      <style>{`@keyframes xlate-spin{to{transform:rotate(360deg)}}`}</style>

      {/* Translation confirm modal */}
      {xlateConfirmPending && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '28px 32px', maxWidth: 440, width: '90%' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>Local Translation</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, marginBottom: 8 }}>Translation runs entirely on your device using a downloaded AI model.</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#86efac', marginBottom: 14 }}>No text or data is sent to any server.</div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, marginBottom: 24 }}>Translating a large thread may take several minutes.</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setXlateConfirmPending(null)}
                style={{ padding: '8px 18px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => {
                const p = xlateConfirmPending;
                xlateConfirmedRef.current = true;
                setXlateConfirmPending(null);
                flushSync(() => {
                  setMsgTranslateOn(prev => ({ ...prev, [p.msgKey]: { ...(prev[p.msgKey] || {}), [p.threadIdx]: true } }));
                });
                batchTranslateTexts(
                  p.thread.messages.filter(m => m.body && !m.removed)
                    .map(m => ({ text: m.body, cacheKey: m.body }))
                );
              }}
                style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: '#be185d', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Translate Locally
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navbar */}
      <div style={{ height: 58, background: '#0a0f1e', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', padding: '0 32px', gap: 12, flexShrink: 0 }}>
        <a href="/" style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px', textDecoration: 'none' }}>LE Tech Tools</a>
        <span style={{ color: C.border, fontSize: 18, fontWeight: 300, margin: '0 4px' }}>/</span>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#f9a8d4' }}>Instagram Warrant Processor</span>
        {(summary.vanity || summary.name) && (
          <>
            <span style={{ color: C.border, fontSize: 18, fontWeight: 300, margin: '0 4px' }}>/</span>
            <span style={{ fontSize: 13, color: C.muted }}>@{summary.vanity || summary.name}</span>
          </>
        )}
        {!hasMediaLoaded && (
          <button onClick={() => { const inp = document.createElement('input'); inp.type = 'file'; inp.multiple = true; inp.webkitdirectory = true; inp.onchange = e => handleMediaFiles(e.target.files); inp.click(); }}
            style={{ marginLeft: 'auto', fontSize: 12, color: C.muted, background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>
            + Load media
          </button>
        )}
        {hasMediaLoaded && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#86efac' }}>
            ✓ Media loaded
          </span>
        )}
        <button onClick={handleNewFile}
          style={{ fontSize: 13, color: '#64748b', background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '5px 12px', cursor: 'pointer', marginLeft: hasMediaLoaded ? 'auto' : 0 }}>
          ← New File
        </button>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <aside style={{ width: 240, background: C.surface, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}` }}>
            <input type="text" placeholder="Filter sections…" value={sidebarFilter}
              onChange={e => setSidebarFilter(e.target.value)}
              style={{ width: '100%', background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, color: C.text, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
            <NavBtn label="Overview" active={activeSection === 'overview'} onClick={() => setActiveSection('overview')} />
            <div style={{ height: 1, background: C.border, margin: '8px 12px' }} />
            {filteredSidebar.map(k => (
              <NavBtn key={k} label={sections[k].label} badge={sections[k].count}
                active={activeSection === k} onClick={() => setActiveSection(k)} />
            ))}
            {analysisInput && (
              <>
                <div style={{ height: 1, background: C.border, margin: '8px 12px' }} />
                <div style={{ padding: '4px 18px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: C.muted }}>
                  Analysis <span title="Generated by LE Tech Tools. Not part of the original legal return." style={{ color: '#fbbf24' }}>✱</span>
                </div>
                {socialNav(analysisInput).filter(s => !sidebarFilter || s.label.toLowerCase().includes(sidebarFilter.toLowerCase())).map(s => (
                  <NavBtn key={s.key} label={s.label} badge={s.count} active={activeSection === s.key} onClick={() => setActiveSection(s.key)} />
                ))}
              </>
            )}
          </div>
        </aside>

        {/* Main content */}
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {isAnalysisPage(activeSection) && analysisInput && <SocialAnalysis page={activeSection} input={analysisInput} />}

          {/* ── Overview ── */}
          {activeSection === 'overview' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>Overview</div>
              <ReviewCaveat dark what="the actual returns from Meta (Instagram)" />
              {top_ips.length > 0 && <GeneratedLegend dark>Generated by LE Tech Tools (the Top 10 IP ranking). Not part of the original legal return. Everything else on this page comes from the return.</GeneratedLegend>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12 }}>
                <StatCard number={followers.length}  label="Followers" highlight />
                <StatCard number={following.length}  label="Following" highlight />
                <StatCard number={photos.length}     label="Photos" highlight />
                <StatCard number={videos.length}     label="Videos" highlight />
                <StatCard number={totalThreads}      label="DM Threads" highlight />
                <StatCard number={ip_records.length} label="IP Records" highlight />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <InfoCard title="Account Information">
                  {[
                    { label: 'Username',        value: summary.vanity,            show: summary.vanity,            mono: true },
                    { label: 'Name',            value: summary.name,              show: summary.name },
                    { label: 'Instagram ID',    value: subject_id,                show: subject_id,                mono: true },
                    { label: 'Date of Birth',   value: summary.date_of_birth,     show: summary.date_of_birth },
                    { label: 'Gender',          value: summary.gender,            show: summary.gender },
                    { label: 'Email',           value: summary.emails,            show: summary.emails },
                    { label: 'Phone',           value: summary.phone_numbers,     show: summary.phone_numbers },
                    { label: 'Website',         value: summary.website,           show: summary.website },
                    { label: 'Registered',      value: summary.registration_date, show: summary.registration_date },
                    { label: 'Registration IP', value: summary.registration_ip,   show: summary.registration_ip,   mono: true },
                    { label: 'Account Active',  value: summary.account_active,    show: summary.account_active },
                    { label: 'Bio',             value: summary.about_me,          show: summary.about_me },
                  ].filter(r => r.show).map((r, i) => (
                    <InfoRow key={r.label} label={r.label} value={r.value} mono={r.mono} first={i === 0} />
                  ))}
                </InfoCard>
                {top_ips.length > 0 && (
                  <InfoCard title={<>Top 10 IP Addresses<GeneratedMark dark /></>}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '7px 0', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: C.muted, borderBottom: `2px solid ${C.border}` }}>IP Address</th>
                          <th style={{ textAlign: 'right', padding: '7px 0', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: C.muted, borderBottom: `2px solid ${C.border}`, width: 50 }}>Hits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {top_ips.map(({ ip, count }) => (
                          <tr key={ip}>
                            <td style={{ padding: '6px 0', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', color: C.cyan }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ height: 6, background: C.pink, borderRadius: 3, width: Math.max(4, Math.round(count / top_ips[0].count * 70)), flexShrink: 0 }} />
                                {ip}
                              </div>
                            </td>
                            <td style={{ padding: '6px 0', borderBottom: `1px solid ${C.border}`, color: C.muted, textAlign: 'right' }}>{count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </InfoCard>
                )}
              </div>
            </div>
          )}

          {/* ── IP Addresses ── */}
          {activeSection === 'ip_addresses' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>IP Addresses</div>
              <input type="text" placeholder="Filter by IP or action…" value={ipFilter}
                onChange={e => setIpFilter(e.target.value)}
                style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 12px', fontSize: 13, color: C.text, outline: 'none', width: '100%', boxSizing: 'border-box' }} />
              {(() => {
                const filtered = ipFilter
                  ? ip_records.filter(r => `${r.ip} ${r.time} ${r.action}`.toLowerCase().includes(ipFilter.toLowerCase()))
                  : ip_records;
                const COLS = ['ip','time','action'];
                const sorted = ipSort
                  ? [...filtered].sort((a, b) => ipSort.asc
                    ? a[COLS[ipSort.col]].localeCompare(b[COLS[ipSort.col]])
                    : b[COLS[ipSort.col]].localeCompare(a[COLS[ipSort.col]]))
                  : filtered;
                return (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr>
                            {['IP Address','Time','Action'].map((h, col) => (
                              <th key={h}
                                onClick={() => setIpSort(prev => ({ col, asc: prev?.col === col ? !prev.asc : true }))}
                                style={{ textAlign: 'left', padding: '8px 12px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: `2px solid ${C.border}`, cursor: 'pointer', userSelect: 'none' }}>
                                {h} ↕
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map((r, i) => (
                            <tr key={i}>
                              <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', color: C.cyan }}>{r.ip}</td>
                              <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}` }}>{r.time}</td>
                              <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}` }}>{r.action}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted }}>{sorted.length} record{sorted.length !== 1 ? 's' : ''}{ipFilter ? ' (filtered)' : ''}</div>
                  </>
                );
              })()}
            </div>
          )}

          {/* ── Followers / Following / Follow Requests ── */}
          {(activeSection === 'followers' || activeSection === 'following' || activeSection === 'incoming_follow_requests') && (() => {
            const listMap = { followers, following, incoming_follow_requests: followReqs };
            const list     = listMap[activeSection] || [];
            const filtered = followerFilter
              ? list.filter(f => f.raw.toLowerCase().includes(followerFilter.toLowerCase()))
              : list;
            return (
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
                  {sections[activeSection].label} — {list.length}
                </div>
                <input type="text" placeholder="Filter…" value={followerFilter}
                  onChange={e => setFollowerFilter(e.target.value)}
                  style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 12px', fontSize: 13, color: C.text, outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px 12px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: `2px solid ${C.border}` }}>Account</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: `2px solid ${C.border}` }}>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((f, i) => (
                        <tr key={i}>
                          <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', fontSize: 12 }}>{f.raw}</td>
                          <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, color: C.muted }}>{f.timestamp}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 12, color: C.muted }}>{filtered.length} account{filtered.length !== 1 ? 's' : ''}{followerFilter ? ' (filtered)' : ''}</div>
              </div>
            );
          })()}

          {/* ── Photos / Profile Picture ── */}
          {(activeSection === 'photos' || activeSection === 'profile_picture') && (() => {
            const photoList = activeSection === 'profile_picture' ? profilePic : photos;
            return (
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
                <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10, marginBottom: 20 }}>
                  {sections[activeSection].label} — {photoList.length}
                  {!hasMediaLoaded && (
                    <span style={{ fontSize: 12, color: C.muted, fontWeight: 400, marginLeft: 12 }}>
                      (load linked_media folder above to display images)
                    </span>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                  {photoList.map((photo, i) => {
                    const imgSrc = resolveMedia(photo.src, mediaMap);
                    return (
                      <div key={i} style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                        {imgSrc
                          ? <img src={imgSrc} alt={photo.id || `photo-${i}`}
                              style={{ width: '100%', maxHeight: 260, objectFit: 'cover', display: 'block' }} />
                          : (
                            <div style={{ height: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: C.bg, color: C.muted, fontSize: 12, gap: 6 }}>
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                              <span style={{ fontSize: 10, maxWidth: 220, textAlign: 'center', wordBreak: 'break-all' }}>{photo.src}</span>
                            </div>
                          )
                        }
                        <div style={{ padding: '12px 14px', fontSize: 12 }}>
                          {photo.taken    && <div style={{ color: C.muted, marginBottom: 4 }}><span style={{ color: C.text, fontWeight: 600 }}>Taken:</span> {photo.taken}</div>}
                          {photo.privacy  && <div style={{ color: C.muted, marginBottom: 4 }}><span style={{ color: C.text, fontWeight: 600 }}>Privacy:</span> {photo.privacy}</div>}
                          {photo.status   && <div style={{ color: C.muted, marginBottom: 4 }}><span style={{ color: C.text, fontWeight: 600 }}>Status:</span> {photo.status}</div>}
                          {photo.uploadIp && <div style={{ color: C.cyan,  fontFamily: 'monospace', fontSize: 11, marginBottom: 4 }}><span style={{ color: C.text, fontWeight: 600, fontFamily: 'inherit' }}>Upload IP:</span> {photo.uploadIp}</div>}
                          {photo.url      && <div><a href={photo.url} target="_blank" rel="noopener noreferrer" style={{ color: C.pink, fontSize: 11 }}>{photo.url}</a></div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── Videos / Live Videos / Archived Live ── */}
          {(activeSection === 'videos' || activeSection === 'live_videos' || activeSection === 'archived_live_videos') && (() => {
            const vidListMap = { videos, live_videos: liveVideos, archived_live_videos: archivedLive };
            const vidList = vidListMap[activeSection] || [];
            return (
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
                <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10, marginBottom: 20 }}>
                  {sections[activeSection].label} — {vidList.length}
                  {!hasMediaLoaded && (
                    <span style={{ fontSize: 12, color: C.muted, fontWeight: 400, marginLeft: 12 }}>
                      (load linked_media folder above to play videos)
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {vidList.map((vid, i) => {
                    const vidSrc = resolveMedia(vid.src, mediaMap);
                    return (
                      <div key={i} style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                        {vidSrc
                          ? <video controls style={{ width: '100%', maxHeight: 360, display: 'block', background: '#000' }}>
                              <source src={vidSrc} type="video/mp4" />
                            </video>
                          : (
                            <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg, color: C.muted, fontSize: 12, gap: 8 }}>
                              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                              <span style={{ wordBreak: 'break-all', maxWidth: 300 }}>{vid.src}</span>
                            </div>
                          )
                        }
                        <div style={{ padding: '12px 14px', fontSize: 12, display: 'flex', flexWrap: 'wrap', gap: '4px 20px' }}>
                          {vid.taken    && <div style={{ color: C.muted }}><span style={{ color: C.text, fontWeight: 600 }}>Taken:</span> {vid.taken}</div>}
                          {vid.privacy  && <div style={{ color: C.muted }}><span style={{ color: C.text, fontWeight: 600 }}>Privacy:</span> {vid.privacy}</div>}
                          {vid.status   && <div style={{ color: C.muted }}><span style={{ color: C.text, fontWeight: 600 }}>Status:</span> {vid.status}</div>}
                          {vid.uploadIp && <div style={{ color: C.cyan, fontFamily: 'monospace', fontSize: 11 }}><span style={{ color: C.text, fontWeight: 600, fontFamily: 'inherit' }}>Upload IP:</span> {vid.uploadIp}</div>}
                          {vid.url      && <div><a href={vid.url} target="_blank" rel="noopener noreferrer" style={{ color: C.pink, fontSize: 11 }}>{vid.url}</a></div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── Devices ── */}
          {activeSection === 'devices' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
                Devices — {devices.length}
              </div>
              {devices.map((dev, i) => (
                <div key={i} style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Device {i + 1} — {dev.type}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
                    <span style={{ color: C.text, fontWeight: 600 }}>ID:</span>{' '}
                    <span style={{ fontFamily: 'monospace', color: C.cyan }}>{dev.id}</span>
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: dev.users.length ? 10 : 0 }}>
                    <span style={{ color: C.text, fontWeight: 600 }}>Active:</span> {dev.active}
                  </div>
                  {dev.users.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: C.muted, marginBottom: 6 }}>Accounts seen on this device</div>
                      {dev.users.map((u, j) => (
                        <div key={j} style={{ fontSize: 13, padding: '5px 0', borderTop: j === 0 ? 'none' : `1px solid ${C.border}` }}>{u}</div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Comments / Comment Likes ── */}
          {(activeSection === 'comments' || activeSection === 'comment_likes') && (() => {
            const list = activeSection === 'comments' ? comments : commentLikes;
            const filtered = commentFilter
              ? list.filter(c => `${c.text} ${c.owner} ${c.dateCreated}`.toLowerCase().includes(commentFilter.toLowerCase()))
              : list;
            return (
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
                  {sections[activeSection].label} — {list.length}
                </div>
                <input type="text" placeholder="Filter by text or owner…" value={commentFilter}
                  onChange={e => setCommentFilter(e.target.value)}
                  style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 12px', fontSize: 13, color: C.text, outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        {['Date','Text','Status','Privacy','Media Owner'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((c, i) => (
                        <tr key={i}>
                          <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap', color: C.muted }}>{c.dateCreated}</td>
                          <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, maxWidth: 300, wordBreak: 'break-word' }}>{c.text}</td>
                          <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{c.status}</td>
                          <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{c.privacy}</td>
                          <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}` }}>{c.owner}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 12, color: C.muted }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}{commentFilter ? ' (filtered)' : ''}</div>
              </div>
            );
          })()}

          {/* ── Likes ── */}
          {activeSection === 'likes' && (() => {
            const filtered = likesFilter
              ? likes.filter(l => `${l.author} ${l.likeTimestamp} ${l.url}`.toLowerCase().includes(likesFilter.toLowerCase()))
              : likes;
            return (
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
                  Likes — {likes.length}
                </div>
                <input type="text" placeholder="Filter by username or URL…" value={likesFilter}
                  onChange={e => setLikesFilter(e.target.value)}
                  style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 12px', fontSize: 13, color: C.text, outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        {['Liked At','Post Author','Status','Post URL'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((l, i) => (
                        <tr key={i}>
                          <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap', color: C.muted }}>{l.likeTimestamp}</td>
                          <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', fontSize: 12 }}>{l.author}</td>
                          <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{l.status}</td>
                          <td style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}` }}>
                            {l.url
                              ? <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: C.pink, fontSize: 11 }}>{l.url}</a>
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 12, color: C.muted }}>{filtered.length} like{filtered.length !== 1 ? 's' : ''}{likesFilter ? ' (filtered)' : ''}</div>
              </div>
            );
          })()}

          {/* ── Message sections ── */}
          {MESSAGE_SECTION_KEYS.map(msgKey => {
            if (activeSection !== msgKey || !message_threads[msgKey]?.length) return null;
            const threads      = message_threads[msgKey];
            const selectedIdx  = selectedThreads[msgKey] ?? 0;
            const thread       = threads[selectedIdx];
            const search       = chatSearches[msgKey] || '';
            const globalSearch = globalChatSearches[msgKey] || '';
            const globalMatchCounts = globalSearch
              ? threads.map(t => t.messages.filter(m =>
                  m.body.toLowerCase().includes(globalSearch.toLowerCase()) ||
                  m.author.toLowerCase().includes(globalSearch.toLowerCase())
                ).length)
              : null;
            const visibleThreadIndices = globalSearch
              ? threads.map((_, i) => i).filter(i => globalMatchCounts[i] > 0)
              : threads.map((_, i) => i);
            const msgs = thread
              ? (search
                ? thread.messages.filter(m => m.body.toLowerCase().includes(search.toLowerCase()) || m.author.toLowerCase().includes(search.toLowerCase()))
                : thread.messages)
              : [];
            return (
              <div key={msgKey} style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '24px 28px', gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10, flexShrink: 0 }}>
                  {sections[msgKey].label} — {threads.length} thread{threads.length !== 1 ? 's' : ''}
                </div>
                <div style={{ flexShrink: 0, position: 'relative' }}>
                  <input type="text" placeholder="Search all conversations…" value={globalSearch}
                    onChange={e => { setGlobalChatSearches(prev => ({ ...prev, [msgKey]: e.target.value })); setChatSearches(prev => ({ ...prev, [msgKey]: '' })); }}
                    style={{ width: '100%', background: C.surf2, border: `1px solid ${globalSearch ? C.cyan : C.border}`, borderRadius: 6, padding: '7px 12px 7px 32px', fontSize: 13, color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={globalSearch ? C.cyan : C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  {globalSearch && (
                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: C.muted }}>
                      {visibleThreadIndices.length} thread{visibleThreadIndices.length !== 1 ? 's' : ''} match
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
                  <div style={{ width: 240, flexShrink: 0, background: C.surf2, borderRadius: 8, border: `1px solid ${C.border}`, overflowY: 'auto' }}>
                    {visibleThreadIndices.map(i => {
                      const t = threads[i];
                      const name = t.thread_name || (t.participants[0] ? t.participants[0].slice(0, 35) : `Thread ${t.thread_id}`);
                      const active = i === selectedIdx;
                      const matchCount = globalMatchCounts ? globalMatchCounts[i] : null;
                      return (
                        <div key={t.thread_id}
                          onClick={() => { setSelectedThreads(prev => ({ ...prev, [msgKey]: i })); setChatSearches(prev => ({ ...prev, [msgKey]: globalSearch })); }}
                          style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', background: active ? C.pink : 'transparent' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: active ? '#fff' : C.text }}>{name}</div>
                          <div style={{ fontSize: 11, color: active ? 'rgba(255,255,255,.8)' : C.muted, marginTop: 2 }}>
                            {matchCount !== null ? <><span style={{ color: active ? '#fce7f3' : C.cyan, fontWeight: 600 }}>{matchCount} match{matchCount !== 1 ? 'es' : ''}</span>{' · '}</> : null}
                            {t.messages.length} msg{t.messages.length !== 1 ? 's' : ''} · ID {t.thread_id}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.surf2, borderRadius: 8, border: `1px solid ${C.border}`, overflow: 'hidden', minWidth: 0 }}>
                    {thread && (
                      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600 }}>{thread.thread_name || thread.participants[0] || `Thread ${thread.thread_id}`}</div>
                          <div style={{ fontSize: 11, color: C.muted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{thread.participants.join(' · ')}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                          <button onClick={() => exportThreadPDF(thread, msgKey, selectedIdx)} disabled={pdfExporting}
                            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, cursor: pdfExporting ? 'not-allowed' : 'pointer', opacity: pdfExporting ? 0.55 : 1 }}>
                            {pdfExporting ? 'Exporting…' : '⬇ Export PDF'}
                          </button>
                          {(() => {
                            const isOn = !!(msgTranslateOn[msgKey]?.[selectedIdx]);
                            const translatableMsgs = thread.messages.filter(m => m.body && !m.removed);
                            const doneMsgs = translatableMsgs.filter(m => xlateCache[m.body] && xlateCache[m.body] !== 'pending').length;
                            const totalMsgs = translatableMsgs.length;
                            return (
                              <button onClick={() => {
                                if (!isOn) {
                                  if (xlateConfirmedRef.current) {
                                    flushSync(() => { setMsgTranslateOn(prev => ({ ...prev, [msgKey]: { ...(prev[msgKey] || {}), [selectedIdx]: true } })); });
                                    batchTranslateTexts(thread.messages.filter(m => m.body && !m.removed).map(m => ({ text: m.body, cacheKey: m.body })));
                                  } else {
                                    setXlateConfirmPending({ type: 'msg', msgKey, threadIdx: selectedIdx, thread });
                                  }
                                } else {
                                  setMsgTranslateOn(prev => ({ ...prev, [msgKey]: { ...(prev[msgKey] || {}), [selectedIdx]: false } }));
                                }
                              }}
                                style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: `1px solid ${isOn ? C.cyan : C.border}`, background: isOn ? 'rgba(103,232,249,0.12)' : 'transparent', color: isOn ? C.cyan : C.muted, cursor: 'pointer' }}>
                                {xlateModelStatus === 'loading'
                                  ? `Loading… ${xlateModelProgress > 0 ? xlateModelProgress + '%' : ''}`
                                  : isOn && doneMsgs < totalMsgs ? `${doneMsgs}/${totalMsgs}`
                                  : isOn ? 'Translate ON' : 'Translate'}
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                    <div style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
                      <input type="text" placeholder="Search messages…" value={search}
                        onChange={e => setChatSearches(prev => ({ ...prev, [msgKey]: e.target.value }))}
                        style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {msgs.length === 0
                        ? <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: 40 }}>{search ? 'No messages match.' : 'No messages in this thread.'}</div>
                        : msgs.map((msg, i) => {
                          const isMine = subject_id && msg.author.includes(subject_id);
                          const isXlateOn = !!(msgTranslateOn[msgKey]?.[selectedIdx]);
                          const xlation = isXlateOn ? xlateCache[msg.body] : undefined;
                          return (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', maxWidth: '72%', alignSelf: isMine ? 'flex-end' : 'flex-start', alignItems: isMine ? 'flex-end' : 'flex-start' }}>
                              {!isMine && <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{msg.author}</div>}
                              <div style={{ padding: '8px 12px', borderRadius: 12, borderBottomRightRadius: isMine ? 3 : 12, borderBottomLeftRadius: isMine ? 12 : 3, fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word', background: isMine ? C.pink : C.surface, fontStyle: msg.removed ? 'italic' : 'normal', opacity: msg.removed ? 0.55 : 1 }}>
                                {msg.removed ? '[removed by sender]' : (msg.body || '[no text]')}
                                {isXlateOn && !msg.removed && msg.body && (xlation === 'pending' || !!xlation) && (
                                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.15)', fontSize: 11, fontStyle: 'italic', lineHeight: 1.45, color: isMine ? 'rgba(255,255,255,0.65)' : C.muted }}>
                                    {xlation === 'pending' ? '⟳ translating…' : xlation}
                                  </div>
                                )}
                              </div>
                              {msg.sent && <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>{msg.sent}</div>}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Generic KV sections ── */}
          {SECTION_KEYS.map(key => {
            if (activeSection !== key) return null;
            const sec = sections[key];
            if (!sec?.count || sec.is_message_section) return null;
            if (IP_KEYS.has(key) || MEDIA_SECTION_KEYS.has(key) || FOLLOWER_SECTION_KEYS.has(key) ||
                COMMENT_SECTION_KEYS.has(key) || LIKES_SECTION_KEYS.has(key) || DEVICES_SECTION_KEYS.has(key)) return null;
            return (
              <div key={key} style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>{sec.label}</div>
                {sec.kv_pairs.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {sec.kv_pairs.map((row, i) => {
                        const isSep = i > 0 && row.record !== sec.kv_pairs[i - 1].record;
                        return (
                          <tr key={i} style={{ borderTop: isSep ? `2px solid ${C.pink}` : `1px solid ${C.border}` }}>
                            <td style={{ padding: '8px 12px', verticalAlign: 'top', fontSize: 13, color: C.muted, width: 200, whiteSpace: 'nowrap' }}>{row.label}</td>
                            <td style={{ padding: '8px 12px', verticalAlign: 'top', fontSize: 13, wordBreak: 'break-word' }}>{row.value}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ color: C.muted, fontSize: 13 }}>No structured data available.</p>
                )}
              </div>
            );
          })}

        </main>
      </div>
    </div>
  );
}

export default InstagramScan;
